import { ArrowLeft2, Gallery } from 'iconsax-react';
import jsQR from 'jsqr';
import { type ChangeEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useToggle } from 'usehooks-ts';
import QrCodeImage from '@/assets/images/icons/qr-code.svg?react';
import BarcodeScanner from '@/components/scan-code/barcode-scanner';
import MyCodeFlyout from '@/components/scan-code/my-code-flyout';
import { useScreenOrientation } from '@/hooks/use-user-platform';
import ROUTES from '@/routes';
import { addTranslateToast } from '@/stores/toast';
import copyTextToClipboard from '@/utils/copy-text';
import { parseUrl } from '@/utils/parse-url';

export default function ScanCode() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const orientation = useScreenOrientation();
  const [isMyCodeOpen, , setMyCodeOpen] = useToggle(false);
  const [scanResult, setScanResult] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleScanResult = (text: string) => {
    const url = parseUrl(text);

    if (!url) return;

    const codeType = url.searchParams.get('qrcodetype');
    if (!codeType) return;

    switch (codeType.toLowerCase()) {
      case 'g':
      case '66chatgroup':
        navigate(ROUTES.CONTACT.REQUEST.GROUP.$buildPath({ searchParams: { code: text } }));
        break;
      case 'u':
      case '66':
        navigate(ROUTES.CONTACT.REQUEST.USER.$buildPath({ searchParams: { code: text, source: 'qrcode' } }));
        break;
      case 'registration':
        navigate(ROUTES.CONTACT.REQUEST.INVITE.$buildPath({ searchParams: { code: text.split('/').at(-1) } }));
        break;
      default:
        setScanResult(text);
        break;
    }
  };

  const handleScanFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target?.files?.[0];

    if (!file) throw 'INVALID_FILE';

    try {
      handleScanResult(await readCodeFromFile(file));
    } catch (err) {
      console.error(err);

      addTranslateToast({
        type: 'error',
        message: 'errors.invalid-format',
      });
    }
  };

  const handlePickFile = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className='relative flex w-screen flex-1 flex-col landscape:flex-row items-stretch justify-center h-screen-safe'>
      <div className='absolute inset-0'>
        <BarcodeScanner onScan={handleScanResult} />
      </div>

      {orientation === 'landscape' && (
        <div className='absolute inset-0 bg-cover z-40 bg-center backdrop-blur-lg bg-neutral-950/25 flex flex-center text-neutral-0'>
          {t('contact.scan-code.incorrect-orientation')}
        </div>
      )}

      <button
        type='button'
        className='btn btn-square btn-ghost absolute left-safe-offset-4 top-safe-offset-4 z-50 hover:shadow'
        onClick={() => navigate(-1)}>
        <ArrowLeft2
          size={24}
          variant='Outline'
          className='aspect-square size-6 text-slate-200'
        />
      </button>

      <div className='m-auto size-70dvw landscape:size-50dvh relative z-10 aspect-square rounded-3xl border-4 border-slate-200 bg-transparent shadow-[0_0_1px_100vw_rgba(0,0,0,.5)]'>
        <p className='absolute inset-x-0 top-full translate-y-8 text-center align-middle text-slate-200'>
          {t('contact.scan-code.alert')}
        </p>

        {scanResult && (
          <div className='absolute inset-x-0 top-full translate-y-1/4 flex z-30 w-full items-center gap-2 rounded bg-neutral-0 p-2'>
            <code className='flex-1 truncate p-2'>{scanResult}</code>

            <button
              type='button'
              className='btn btn-primary'
              onClick={() => copyTextToClipboard(scanResult)}>
              {t('conversation.copy')}
            </button>
          </div>
        )}
      </div>

      <div className='z-20 flex flex-col gap-8 landscape:pe-safe-offset-8 portrait:pb-safe-offset-8'>
        <div className='flex landscape:flex-col landscape:flex-1 justify-evenly text-slate-200'>
          <button
            type='button'
            className='flex flex-col items-center gap-2'
            onClick={() => setMyCodeOpen(true)}>
            <QrCodeImage className='size-6' />

            <span className='text-body1'>{t('contact.scan-code.my-code')}</span>
          </button>

          <button
            type='button'
            className='flex flex-col items-center gap-2'
            onClick={handlePickFile}>
            <Gallery
              variant='Bold'
              size={24}
              className='size-6'
            />

            <span className='text-body1'>{t('upload.album')}</span>
          </button>
        </div>

        <input
          type='file'
          ref={fileInputRef}
          className='hidden'
          accept='image/*'
          onChange={handleScanFile}
        />
      </div>

      <MyCodeFlyout
        open={isMyCodeOpen}
        onClose={() => setMyCodeOpen(false)}
      />
    </div>
  );
}

async function readCodeFromFile(file: File): Promise<string> {
  const imageData = await loadImage(file);
  const result = jsQR(imageData.data, imageData.width, imageData.height);

  if (result?.data) {
    return result.data;
  }

  throw 'READ_CODE_FAILED';
}

async function loadImage(file: File): Promise<ImageData> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);

    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        img.src = e.target.result as string;
      }
    };
    reader.onerror = (err) => {
      reject(err);
    };

    reader.readAsDataURL(file);
  });

  return new Promise((resolve, reject) => {
    const canvas = new OffscreenCanvas(image.width, image.height);
    const context = canvas.getContext('2d');

    if (context) {
      context.drawImage(image, 0, 0);

      resolve(context.getImageData(0, 0, canvas.width, canvas.height));
    } else {
      reject('Failed to create canvas context');
    }
  });
}
